import { useState, useCallback } from 'react'
import SearchBar from '@/components/SearchBar'
import FilterTabs from '@/components/FilterTabs'
import ResultList from '@/components/ResultList'
import LawViewer from '@/components/LawViewer'
import { searchLaw } from '@/lib/api'
import type { SearchResult, LawType } from '@/types/law'

type FilterType = LawType | 'all'

export default function App() {
  const [query, setQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)

  const handleSearch = useCallback(async (q: string) => {
    setQuery(q)
    setIsLoading(true)
    setError(null)
    setSelectedResult(null)

    try {
      const { results: res, total } = await searchLaw(q, filterType)
      setResults(res)
      setTotalCount(total)
    } catch (e) {
      setError(e instanceof Error ? e.message : '검색 중 오류가 발생했습니다.')
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [filterType])

  const handleFilterChange = (type: FilterType) => {
    setFilterType(type)
    if (query) handleSearch(query)
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 font-sans">
      {/* ─── 헤더 ────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="max-w-screen-xl mx-auto flex items-center gap-6">
          {/* 로고 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-2xl">⚖️</span>
            <div>
              <span className="text-xl font-black text-law-700 tracking-tight">klaw</span>
              <span className="ml-1.5 text-xs text-gray-400 font-medium">법령 통합검색</span>
            </div>
          </div>

          {/* 검색바 */}
          <div className="flex-1">
            <SearchBar onSearch={handleSearch} isLoading={isLoading} />
          </div>
        </div>
      </header>

      {/* ─── 메인 레이아웃 (3패널) ──────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden max-w-screen-xl w-full mx-auto flex">

        {/* 왼쪽: 결과 패널 */}
        <aside className="w-80 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
          {/* 필터 탭 */}
          <div className="p-3 border-b border-gray-100">
            <FilterTabs active={filterType} onChange={handleFilterChange} />
          </div>

          {/* 결과 카운트 */}
          {query && !isLoading && (
            <div className="px-4 py-2 text-xs text-gray-400 border-b border-gray-50">
              {error
                ? <span className="text-red-500">오류 발생</span>
                : <span><strong className="text-gray-600">{totalCount.toLocaleString()}</strong>건 검색됨</span>
              }
            </div>
          )}

          {/* 에러 */}
          {error && (
            <div className="mx-3 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
              {error}
            </div>
          )}

          {/* 결과 리스트 */}
          <div className="flex-1 overflow-y-auto p-3">
            <ResultList
              results={results}
              selectedId={selectedResult?.id}
              onSelect={setSelectedResult}
              isLoading={isLoading}
              query={query}
            />
          </div>
        </aside>

        {/* 오른쪽: 법령 뷰어 */}
        <section className="flex-1 overflow-hidden bg-white">
          <LawViewer result={selectedResult} />
        </section>
      </main>

      {/* ─── 푸터 ─────────────────────────────────────────────────────── */}
      <footer className="bg-white border-t border-gray-100 px-6 py-2 flex-shrink-0">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <span>데이터 출처: <a href="https://www.law.go.kr" target="_blank" rel="noopener noreferrer"
            className="hover:text-law-500">국가법령정보센터 (law.go.kr)</a></span>
          <span>klaw v0.1.0 · <a href="https://github.com/chrisryugj/korean-law-mcp"
            target="_blank" rel="noopener noreferrer" className="hover:text-law-500">korean-law-mcp</a> 기반</span>
        </div>
      </footer>
    </div>
  )
}
